import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {SingleCheckBox} from '../../forms/form-control/checkbox-single/SingleCheckBox';
import {CheckboxDropdownStructure} from './CheckboxDropdownStructure';

/**
 * This component displays a list of selectable checkbox options.
 * It allows selecting one or more options.
 *
 * @param {object} param0
 * Component props.
 *
 * @param {string} param0.name
 * The name that will be used as an identifier for the component.
 *
 * @param {Array<{id: any; name: any; checked?: boolean}>} param0.options
 * The list of options to display in the dropdown. Each option should have a unique
 * `id`. The `name` property is displayed as-is. An optional 'checked' property
 * can be used to set the initioal state of checkbox.
 *
 * @param {"top-center" | "top-start" | "top-end" | "left-start" | "left-center" | "left-end" | "right-start" | "right-center" | "right-end" | "bottom-start" | "bottom-center" | "bottom-end" | "center"} [placement]
 *
 * @param {(selection: Array<any>) => void} param0.onSelectionChanged
 * The onSelectionChanged callback provides an array as a parameter to the function.
 * The item is the option.id, if it's in the array that means the options item with that id is selected.
 *
 * @param {(close: () => void) => JSX.Element} param0.footer
 * A function that returns an element to be rendered in the dropdown footer.
 * The function provided as a parameter, closes the dropdown when invoked.
 *
 * @param {JSX.Element} param0.children
 * This component is rendered inside the dropdown toggle button.
 * It can be used to show the selected options, for example.
 */
function RawCheckboxDropdown({name, options, placement, footer, children, onSelectionChanged}) {
    const [checked, setChecked] = useState(options.filter((x) => x.checked).map((x) => x.id));

    const toggleCheck = (inputId) => {
        const newState = checked.includes(inputId) ? _.without(checked, inputId) : [...checked, inputId];
        setChecked(newState);
        onSelectionChanged(newState);
    };

    const selectAll = () => {
        const newState = _.map(options, 'id');
        setChecked(newState);
        onSelectionChanged(newState);
    };

    const deSelectAll = () => {
        setChecked([]);
        onSelectionChanged([]);
    };

    useEffect(() => {
        // Update checked options whenever an update is pushed from a parent component
        setChecked(options.filter((x) => x.checked).map((x) => x.id));
    }, [options]);

    return (
        <CheckboxDropdownStructure
            footer={footer}
            placement={placement}
            onSelectAll={selectAll}
            onDeselectAll={deSelectAll}
            optionList={
                options &&
                options.map((attr) => {
                    return (
                        <SingleCheckBox
                            raw
                            id={`${name}.${attr.id}`}
                            label={attr.name}
                            key={attr.id}
                            value={attr.id}
                            checked={_.some(checked, (x) => x === attr.id)}
                            onChange={() => toggleCheck(attr.id)}
                        />
                    );
                })
            }>
            {children}
        </CheckboxDropdownStructure>
    );
}

export {RawCheckboxDropdown};
